import { useRef, Suspense } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import scrollIntoView from 'scroll-into-view';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import classnames from 'classnames';
import dibsCss from 'dibs-css';

// components
import { SbSharedTitle } from '../SbSharedTitle/SbSharedTitle.tsx';
import { SbSharedPageBreadcrumbs } from '../SbSharedPageBreadcrumbs/SbSharedPageBreadcrumbs.tsx';
import { SharedSaveSearchContextProvider } from '../../shared/SharedSaveSearchContext/SharedSaveSearchContext.tsx';
import { SharedFollowTopButton } from '../../shared/SharedFollowTopButton/SharedFollowTopButton.tsx';
import { SharedCreatorsRootHeadingsWrapper } from '../../shared/SharedCreatorsRootHeadingsWrapper/SharedCreatorsRootHeadingsWrapper.tsx';
import { SharedWordpressArticles } from '../../shared/SharedWordpressArticles/SharedWordpressArticles.jsx';
import { SharedRespLinksBlock } from '../../shared/SharedRespLinksBlock/SharedRespLinksBlock.tsx';
import { SbSharedRelatedCreators } from '../SbSharedRelatedCreators/SbSharedRelatedCreators.tsx';
import { SbRespFollowSearchBannerLazy } from '../SbRespFollowSearchBanner/SbRespFollowSearchBannerLazy.tsx';
import { SharedRelatedPages } from '../../shared/SharedRelatedPages/SharedRelatedPages.jsx';
import { SbRespDesignTiles } from '../SbRespDesignTiles/SbRespDesignTiles.tsx';
import { SbSharedTopTileController } from '../SbSharedTopTileController/SbSharedTopTileController.jsx';
import { SbSharedCategoryTabs } from '../SbSharedCategoryTabs/SbSharedCategoryTabs.tsx';
import { SbSharedCreatorTopContent } from '../SbSharedCreatorTopContent/SbSharedCreatorTopContent.tsx';
import { SbRespCreatorDesigns } from '../SbSharedCreatorDesigns/SbRespCreatorDesigns.tsx';
import { SbSharedRelatedCreatorBuyPages } from '../SbSharedRelatedCreatorBuyPages/SbSharedRelatedCreatorBuyPages.tsx';
import { SharedFAQ } from '../../shared/SharedFAQ/SharedFAQ.tsx';
import { SharedBottomModule } from '../../shared/SharedBottomModule/SharedBottomModule.tsx';
import { SbRespRecentSales } from '../SbRespRecentSales/SbRespRecentSales.tsx';
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy.ts';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency.tsx';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';

// constants
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes.ts';
import { ICONIC_DESIGN_MODULE_TYPE } from '../../constants/contentModuleConstants.js';
import { REVEAL_RELATED_CREATORS_COUNT_DESKTOP } from '../../constants/sbConstants.ts';
import { useUpdateRecentSearchResultEntryFromItemSearch } from '../hooks/useUpdateRecentSearchResultEntryFromItemSearch.ts';

const SbRespCreatorsComponentsComponent = props => {
    const { viewer, itemSearch, user, uriRef, isClient } = props;

    useUpdateRecentSearchResultEntryFromItemSearch({ isClient: !!isClient, itemSearch });

    const recentSalesScrollRef = useRef(null);
    const relatedCreatorsScrollRef = useRef(null);
    const relatedIconicDesignScrollRef = useRef(null);

    const { currency } = useCurrency();
    const { isBot } = useServerVarsContext();
    const { isWordpressArticlesEnabled, isRelatedCreatorsModuleEnabled, isRelatedBuyPagesEnabled } =
        viewer;

    const browseContentModule = itemSearch?.browseContentModule;
    const bottomModule = itemSearch?.bottomModule;
    const showContentModule =
        browseContentModule?.moduleType === ICONIC_DESIGN_MODULE_TYPE &&
        browseContentModule?.title &&
        browseContentModule?.mainImage;

    const scrollToRef = RefObj => {
        const gridEl = RefObj?.current;
        if (gridEl) {
            scrollIntoView(
                gridEl,
                {
                    time: 500,
                },
                () => gridEl.focus()
            );
        }
    };

    return (
        <SharedSaveSearchContextProvider user={user} itemSearch={itemSearch} uriRef={uriRef}>
            <SbSharedPageBreadcrumbs itemSearch={itemSearch} user={user} />
            <SharedCreatorsRootHeadingsWrapper
                itemSearch={itemSearch}
                showContentModule={showContentModule}
            >
                {Heading => (
                    <>
                        {/* Page Header */}
                        <div className={classnames(dibsCss.flex, dibsCss.itemsCenter)}>
                            <SbSharedTitle itemSearch={itemSearch} user={user} Heading={Heading} />
                            <SharedFollowTopButton
                                itemSearch={itemSearch}
                                user={user}
                                addLeadingSpace
                            />
                        </div>
                        {/* Content Module */}
                        <SbSharedCreatorTopContent
                            itemSearch={itemSearch}
                            user={user}
                            viewer={viewer}
                            creatorRootPage={viewer?.creatorRootPage}
                            onClickViewMoreCreators={() => scrollToRef(relatedCreatorsScrollRef)}
                            onClickViewMoreSales={() => scrollToRef(recentSalesScrollRef)}
                            currency={currency}
                        />

                        {/* Broad Search Category Tiles, or, for trade users, Price Range Tiles or On-Approval banner)*/}
                        <SbSharedTopTileController
                            itemSearch={itemSearch}
                            designTiles={
                                <SbRespDesignTiles
                                    itemSearch={itemSearch}
                                    designPageInfo={itemSearch?.designPageInfo}
                                    onViewMoreClick={() =>
                                        scrollToRef(relatedIconicDesignScrollRef)
                                    }
                                />
                            }
                            viewer={viewer}
                        />

                        {/* Results based on verticals */}
                        <SbSharedCategoryTabs
                            viewer={viewer}
                            creatorRootPage={viewer?.creatorRootPage}
                            itemSearch={itemSearch}
                        />

                        {/* Related buy pages for the creator */}
                        {isRelatedBuyPagesEnabled && (
                            <SbSharedRelatedCreatorBuyPages
                                creatorRootPage={viewer?.creatorRootPage}
                            />
                        )}

                        {/* Related transaction data */}
                        <div ref={recentSalesScrollRef}>
                            <SbRespRecentSales
                                creatorRootPage={viewer?.creatorRootPage}
                                itemSearch={itemSearch}
                                currency={currency}
                            />
                        </div>

                        {/* Related Creators for creator pages */}
                        {isRelatedCreatorsModuleEnabled && (
                            <div ref={relatedCreatorsScrollRef}>
                                <SbSharedRelatedCreators
                                    itemSearch={itemSearch}
                                    revealCreatorCount={REVEAL_RELATED_CREATORS_COUNT_DESKTOP}
                                />
                            </div>
                        )}

                        {/*  Creator Designs */}
                        <div ref={relatedIconicDesignScrollRef}>
                            <SbRespCreatorDesigns
                                designPageInfo={itemSearch?.designPageInfo}
                                currency={currency}
                            />
                        </div>

                        {/* Related Wordpress Articles */}
                        {isWordpressArticlesEnabled && (
                            <SharedWordpressArticles
                                itemSearch={itemSearch}
                                launchOrigin={PAGE_TYPE.CREATOR}
                            />
                        )}

                        {/* Follow Search */}
                        {isClient && (
                            <Suspense fallback="">
                                <SbRespFollowSearchBannerLazy itemSearch={itemSearch} user={user} />
                            </Suspense>
                        )}

                        {/* Bottom content module */}
                        <SharedBottomModule
                            className={dibsCss.mbLarger}
                            bottomModule={bottomModule}
                        />

                        {/* Questions & Answers */}
                        <SharedFAQ itemSearch={itemSearch} />

                        {/* Recently Viewed Items */}
                        <RecentlyViewedItemsLazy
                            authModalLoader={authModalLoader}
                            currency={currency}
                            isBot={isBot}
                        />

                        {/* Related buy pages */}
                        <SharedRelatedPages
                            viewer={viewer}
                            itemSearch={itemSearch}
                            LinksBlockComponent={SharedRespLinksBlock}
                        />
                    </>
                )}
            </SharedCreatorsRootHeadingsWrapper>
        </SharedSaveSearchContextProvider>
    );
};

SbRespCreatorsComponentsComponent.propTypes = {
    itemSearch: PropTypes.object.isRequired,
    user: PropTypes.object,
    isClient: PropTypes.bool,
    viewer: PropTypes.object,
    uriRef: PropTypes.string,
    relay: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({ uriRef: state?.relayVariables?.variables?.uriRef });

export const SbRespCreatorsComponents = createFragmentContainer(
    connect(mapStateToProps)(SbRespCreatorsComponentsComponent),
    {
        viewer: graphql`
            fragment SbRespCreatorsComponents_viewer on Viewer {
                isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
                isRelatedCreatorsModuleEnabled: featureFlag(feature: "relatedCreatorsModule")
                isRelatedBuyPagesEnabled: isEnabledByLocale(featureName: "RELATED_BUY_PAGES")
                ...SbSharedCategoryTabs_viewer
                ...SharedRelatedPages_viewer
                ...SharedRespLinksBlock_viewer
                ...SbSharedTopTileController_viewer
                ...SbSharedCreatorTopContent_viewer
                creatorRootPage(uriRef: $uriRef) {
                    ...SbSharedCreatorTopContent_creatorRootPage
                    ...SbSharedCategoryTabs_creatorRootPage
                    ...SbSharedRelatedCreatorBuyPages_creatorRootPage
                    ...SbRespRecentSales_creatorRootPage
                }
            }
        `,
        user: graphql`
            fragment SbRespCreatorsComponents_user on User {
                ...SbRespFollowSearchBanner_user
                ...SharedFollowTopButton_user
                ...SbSharedTitle_user
                ...SharedSaveSearchContext_user
                ...SbSharedPageBreadcrumbs_user
                ...SbSharedCreatorTopContent_user
            }
        `,
        itemSearch: graphql`
            fragment SbRespCreatorsComponents_itemSearch on ItemSearchQueryConnection {
                ...useUpdateRecentSearchResultEntryFromItemSearch_itemSearch
                ...SbSharedPageBreadcrumbs_itemSearch
                ...SbSharedTitle_itemSearch
                ...SbSharedTopTileController_itemSearch
                ...SbSharedRelatedSearches_itemSearch
                ...SbRespDesignTiles_itemSearch
                ...SharedSaveSearchContext_itemSearch
                ...SharedWordpressArticles_itemSearch
                ...SharedFollowTopButton_itemSearch
                ...SharedRelatedPages_itemSearch
                ...SharedRespLinksBlock_itemSearch
                ...SharedCreatorsRootHeadingsWrapper_itemSearch
                ...SbSharedCreatorTopContent_itemSearch
                ...SbSharedRelatedCreators_itemSearch
                ...SbSharedCategoryTabs_itemSearch
                ...SbRespRecentSales_itemSearch
                bottomModule {
                    ...SharedBottomModule_bottomModule
                }
                designPageInfo(maxCreatorDesigns: 16) {
                    ...SbRespCreatorDesigns_designPageInfo
                    ...SbRespDesignTiles_designPageInfo
                }
                ...SharedFAQ_itemSearch
                ...SbRespFollowSearchBanner_itemSearch
            }
        `,
    }
);
